import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, deleteDoc, query, where, writeBatch, serverTimestamp, deleteField } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import {
  FaVideo, FaFilePdf, FaLink, FaDownload, FaExternalLinkAlt, FaPlus,
  FaImage, FaMusic, FaCheck, FaTimes, FaEdit, FaTrash, FaFileExcel,
  FaFileWord, FaFilePowerpoint, FaFileAlt, FaEye, FaPlay, FaChevronLeft,
  FaChevronRight, FaChevronDown, FaChevronUp, FaMobileAlt, FaFolder, FaUserFriends
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../styles/Resources.css';

// ============================================================================
// Centre de ressources (admin)
// ----------------------------------------------------------------------------
// Avant, les fichiers des dossiers et catégories étaient rendus avec des
// classes CSS partagées (resources-list, resource-item…) qui les masquaient :
// on voyait le nombre de ressources mais jamais les fichiers.
// Cette page n'utilise plus que des styles en ligne pour tout ce qui affiche
// des fichiers, et propose :
//   - un onglet "Ressources" : TOUTES les ressources (admin + envoyées depuis
//     l'app) en grille avec aperçu, filtres, recherche, sélection multiple,
//     suppression / approbation / téléchargement groupés ;
//   - un onglet "Dossiers & catégories" pour gérer la structure ;
//   - un aperçu plein écran (photo, vidéo, musique, PDF) avec navigation.
// ============================================================================

const PURPLE = '#7C3AED';
const PAGE_SIZE = 60;

// Supprime le fichier associé dans Firebase Storage (si c'est un fichier Storage).
// Ne bloque jamais la suppression du document Firestore : en cas d'échec on log seulement.
export const deleteStorageFile = async (url) => {
  if (!url || !url.includes('firebasestorage.googleapis.com')) return;
  try {
    await deleteObject(ref(storage, url));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Fichier Storage non supprimé :', err);
    }
  }
};

// Récupère l'extension d'un fichier depuis son nom ou son URL Storage
const getFileExtension = (resource) => {
  const source = resource.fileName || resource.filePath || '';
  try {
    const clean = decodeURIComponent(source.split('?')[0]);
    const last = clean.split('/').pop() || '';
    return last.includes('.') ? last.split('.').pop().toLowerCase() : '';
  } catch (e) {
    return '';
  }
};

// dateAdded peut être un Date, une chaîne ISO (anciennes éditions) ou absent
const toTime = (value) => {
  if (!value) return 0;
  const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const formatDate = (value) => {
  const t = toTime(value);
  if (!t) return 'Date inconnue';
  return new Date(t).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const normalize = (s) => (s || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '');

const isVideo = (r) => r.type === 'Vidéo' || r.type === 'Video en ligne';
const isPdf = (r) => r.type === 'Document PDF' || getFileExtension(r) === 'pdf';
const isFromApp = (r) => r.uploadedByRole === 'student' || r.uploadedByRole === 'teacher';

const TYPE_FILTERS = [
  { key: 'all', label: 'Tout', match: () => true },
  { key: 'image', label: 'Photos', match: (r) => r.type === 'Image' },
  { key: 'video', label: 'Vidéos', match: isVideo },
  { key: 'audio', label: 'Musique', match: (r) => r.type === 'Musique' },
  { key: 'doc', label: 'Documents', match: (r) => ['Document PDF', 'Document', 'Autre'].includes(r.type) },
  { key: 'link', label: 'Liens', match: (r) => r.type === 'Lien externe' },
];

const typeColor = (r) => {
  if (r.type === 'Image') return '#7C3AED';
  if (isVideo(r)) return '#6D28D9';
  if (r.type === 'Musique') return '#DB2777';
  if (r.type === 'Lien externe') return '#2563EB';
  const ext = getFileExtension(r);
  if (isPdf(r)) return '#DC2626';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return '#16A34A';
  if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)) return '#1D4ED8';
  if (['ppt', 'pptx', 'odp'].includes(ext)) return '#EA580C';
  return '#6B7280';
};

const getIconForResource = (resource, size) => {
  const style = size ? { fontSize: size } : undefined;
  switch (resource.type) {
    case 'Video en ligne':
    case 'Vidéo':
      return <FaVideo style={style} />;
    case 'Document PDF':
      return <FaFilePdf style={style} />;
    case 'Lien externe':
      return <FaLink style={style} />;
    case 'Image':
      return <FaImage style={style} />;
    case 'Musique':
      return <FaMusic style={style} />;
    default: {
      const ext = getFileExtension(resource);
      if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return <FaFileExcel style={style} />;
      if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return <FaFileWord style={style} />;
      if (['ppt', 'pptx', 'odp'].includes(ext)) return <FaFilePowerpoint style={style} />;
      if (ext === 'pdf') return <FaFilePdf style={style} />;
      return <FaFileAlt style={style} />;
    }
  }
};

// Nom proposé au téléchargement : titre + extension
const downloadName = (r) => {
  const ext = getFileExtension(r);
  const base = (r.title || r.fileName || 'ressource').replace(/[\\/:*?"<>|]/g, '_').slice(0, 80);
  if (r.fileName && !r.title) return r.fileName;
  return ext && !base.toLowerCase().endsWith(`.${ext}`) ? `${base}.${ext}` : base;
};

// Téléchargement réel (blob). Si le navigateur refuse (CORS non configuré sur
// le bucket Storage), renvoie false et l'appelant ouvre le fichier à la place.
const downloadBlob = async (r) => {
  const res = await fetch(r.filePath);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = downloadName(r);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
};

// ── Aperçu vidéo chargé seulement quand la carte devient visible ────────────
function LazyVideoThumb({ src }) {
  const holder = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = holder.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={holder} style={{ width: '100%', height: '100%', background: '#111', position: 'relative' }}>
      {visible && (
        <video
          src={`${src}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', display: 'block' }}
        />
      )}
      <div style={styles.playBadge}><FaPlay /></div>
    </div>
  );
}

// ── Carte d'une ressource ───────────────────────────────────────────────────
function ResourceCard({
  resource, selected, onToggleSelect, onPreview, onDownload,
  onDelete, onApprove, busy, authorName, location,
}) {
  const r = resource;
  const pending = r.status === 'pending';
  const fromApp = isFromApp(r);

  let preview;
  if (r.type === 'Image' && r.filePath) {
    preview = (
      <img
        src={r.thumbUrl || r.filePath}
        alt={r.title || ''}
        loading="lazy"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  } else if (isVideo(r) && r.filePath) {
    preview = <LazyVideoThumb src={r.filePath} />;
  } else {
    const ext = getFileExtension(r);
    preview = (
      <div style={{ ...styles.iconPreview, background: typeColor(r) }}>
        {getIconForResource(r, 38)}
        {ext && <span style={{ fontSize: 12, fontWeight: 700, marginTop: 6 }}>.{ext.toUpperCase()}</span>}
      </div>
    );
  }

  return (
    <div style={{ ...styles.card, ...(selected ? styles.cardSelected : null) }}>
      <div style={styles.cardPreview} onClick={() => onPreview(r)} title="Aperçu">
        {preview}

        <label
          style={styles.checkbox}
          onClick={(e) => e.stopPropagation()}
          title="Sélectionner"
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(r.id)}
            style={{ width: 18, height: 18, cursor: 'pointer', accentColor: PURPLE }}
          />
        </label>

        <div style={styles.badges}>
          {pending && <span style={{ ...styles.badge, background: '#F59E0B' }}>En attente</span>}
          {fromApp && (
            <span style={{ ...styles.badge, background: '#2563EB' }}>
              <FaMobileAlt /> App
            </span>
          )}
        </div>
      </div>

      <div style={styles.cardBody}>
        <strong style={styles.cardTitle} title={r.title}>{r.title || 'Sans titre'}</strong>
        <span style={styles.cardMeta} title={location}>{location}</span>
        <span style={styles.cardMeta}>
          {authorName ? `${authorName} · ` : ''}{formatDate(r.dateAdded)}
        </span>

        <div style={styles.cardActions}>
          {pending && onApprove && (
            <button type="button" onClick={() => onApprove([r])} disabled={busy}
              style={{ ...styles.iconBtn, color: '#16A34A' }} title="Approuver">
              <FaCheck />
            </button>
          )}
          <button type="button" onClick={() => onPreview(r)} style={styles.iconBtn} title="Aperçu">
            <FaEye />
          </button>
          {(r.filePath || r.link) && (
            <button type="button" onClick={() => onDownload(r)} style={styles.iconBtn}
              title={r.filePath ? 'Télécharger' : 'Ouvrir le lien'}>
              {r.filePath ? <FaDownload /> : <FaExternalLinkAlt />}
            </button>
          )}
          <Link to={`/resources/edit/${r.id}`} style={{ ...styles.iconBtn, textDecoration: 'none' }} title="Modifier / déplacer">
            <FaEdit />
          </Link>
          <button type="button" onClick={() => onDelete([r])} disabled={busy}
            style={{ ...styles.iconBtn, color: '#DC2626', marginLeft: 'auto' }} title="Supprimer">
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Aperçu plein écran ──────────────────────────────────────────────────────
function PreviewModal({ resource, index, total, onClose, onPrev, onNext, onDownload, onDelete, onApprove, location, authorName }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  const r = resource;
  let content;
  if (r.type === 'Image' && r.filePath) {
    content = <img src={r.filePath} alt={r.title || ''} style={styles.modalMedia} />;
  } else if (isVideo(r) && r.filePath) {
    content = <video key={r.id} src={r.filePath} controls autoPlay style={styles.modalMedia} />;
  } else if (r.type === 'Musique' && r.filePath) {
    content = (
      <div style={{ ...styles.modalFallback, gap: 20 }}>
        <div style={{ fontSize: 64, color: '#fff' }}><FaMusic /></div>
        <audio key={r.id} src={r.filePath} controls autoPlay style={{ width: 'min(520px, 90vw)' }} />
      </div>
    );
  } else if (isPdf(r) && r.filePath) {
    content = <iframe key={r.id} src={r.filePath} title={r.title || 'PDF'} style={{ ...styles.modalMedia, width: 'min(1000px, 92vw)', height: '78vh', background: '#fff', border: 'none' }} />;
  } else {
    content = (
      <div style={styles.modalFallback}>
        <div style={{ fontSize: 64, color: '#fff' }}>{getIconForResource(r)}</div>
        <p style={{ color: '#e5e7eb', marginTop: 12 }}>Pas d'aperçu pour ce type de fichier.</p>
        {(r.filePath || r.link) && (
          <button type="button" onClick={() => onDownload(r)} style={{ ...styles.primaryBtn, marginTop: 8 }}>
            {r.filePath ? <><FaDownload /> Télécharger</> : <><FaExternalLinkAlt /> Ouvrir le lien</>}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modalTop} onClick={(e) => e.stopPropagation()}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.title || 'Sans titre'}
          </div>
          <div style={{ fontSize: 13, color: '#d1d5db' }}>
            {location}{authorName ? ` · ${authorName}` : ''} · {formatDate(r.dateAdded)} · {index + 1}/{total}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {r.status === 'pending' && (
            <button type="button" onClick={() => onApprove([r])} style={{ ...styles.modalBtn, background: '#16A34A' }}>
              <FaCheck /> Approuver
            </button>
          )}
          {(r.filePath || r.link) && (
            <button type="button" onClick={() => onDownload(r)} style={styles.modalBtn}>
              <FaDownload /> Télécharger
            </button>
          )}
          <Link to={`/resources/edit/${r.id}`} style={{ ...styles.modalBtn, textDecoration: 'none' }}>
            <FaEdit /> Modifier
          </Link>
          <button type="button" onClick={() => onDelete([r])} style={{ ...styles.modalBtn, background: '#DC2626' }}>
            <FaTrash /> Supprimer
          </button>
          <button type="button" onClick={onClose} style={styles.modalBtn} title="Fermer (Échap)">
            <FaTimes />
          </button>
        </div>
      </div>

      <div style={styles.modalCenter} onClick={(e) => e.stopPropagation()}>
        {content}
      </div>

      {total > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }}
            style={{ ...styles.navBtn, left: 16 }} title="Précédent (←)">
            <FaChevronLeft />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }}
            style={{ ...styles.navBtn, right: 16 }} title="Suivant (→)">
            <FaChevronRight />
          </button>
        </>
      )}
    </div>
  );
}

function Resources() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [folders, setFolders] = useState([]);
  const [resources, setResources] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Onglets / filtres
  const [view, setView] = useState('resources'); // 'resources' | 'structure'
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // all | app | admin
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved
  const [locationFilter, setLocationFilter] = useState('all'); // all | none | folder:id | category:id
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [pendingOpen, setPendingOpen] = useState(true);

  // Sélection + aperçu
  const [selected, setSelected] = useState(() => new Set());
  const [previewId, setPreviewId] = useState(null);
  const [previewList, setPreviewList] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [coursesSnapshot, categoriesSnapshot, foldersSnapshot, resourcesSnapshot] = await Promise.all([
        getDocs(collection(db, 'dance_courses')),
        getDocs(collection(db, 'resource_categories')),
        getDocs(collection(db, 'resource_folders')),
        getDocs(collection(db, 'resources')),
      ]);

      const coursesData = coursesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const categoriesData = categoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const foldersData = foldersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const resourcesData = resourcesSnapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          dateAdded: data.dateAdded?.toDate ? data.dateAdded.toDate() : data.dateAdded,
        };
      });

      // Noms des auteurs (élèves, profs, parents) pour les envois depuis l'app
      const usersSnapshot = await getDocs(query(
        collection(db, 'users'),
        where('role', 'in', ['Élève', 'Élève adulte', 'Professeur', 'Parent'])
      ));
      const usersMap = {};
      usersSnapshot.docs.forEach(d => {
        const data = d.data();
        usersMap[d.id] = `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.pseudo || d.id;
      });

      setCourses(coursesData);
      setCategories(categoriesData);
      setFolders(foldersData);
      setResources(resourcesData);
      setUsersById(usersMap);
      setLoading(false);
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur de chargement');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Index et libellés ─────────────────────────────────────────────────────
  const courseById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c])), [courses]);
  const categoryById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);
  const folderById = useMemo(() => Object.fromEntries(folders.map(f => [f.id, f])), [folders]);

  // Une catégorie peut être partagée entre plusieurs cours (courseIds).
  // Les anciennes catégories n'ont que courseId : on gère les deux.
  const categoryCourseIds = (category) =>
    Array.isArray(category.courseIds) && category.courseIds.length > 0
      ? category.courseIds
      : (category.courseId ? [category.courseId] : []);

  const courseTitle = (courseId) => courseById[courseId]?.title || 'Cours supprimé';

  const locationOf = (r) => {
    if (r.folderId) {
      const f = folderById[r.folderId];
      return f ? `Dossier · ${f.name}` : 'Dossier supprimé';
    }
    if (r.categoryId) {
      const c = categoryById[r.categoryId];
      if (!c) return 'Catégorie supprimée';
      const names = categoryCourseIds(c).map(courseTitle).join(', ');
      return names ? `${names} · ${c.name}` : c.name;
    }
    return 'Sans emplacement';
  };

  const isOrphan = (r) =>
    (r.folderId && !folderById[r.folderId]) ||
    (!r.folderId && r.categoryId && !categoryById[r.categoryId]) ||
    (!r.folderId && !r.categoryId);

  const authorOf = (r) => (isFromApp(r) ? (usersById[r.uploadedBy] || 'Utilisateur inconnu') : '');

  const pendingResources = useMemo(
    () => resources
      .filter(r => r.status === 'pending')
      .sort((a, b) => toTime(b.dateAdded) - toTime(a.dateAdded)),
    [resources]
  );

  // Images sans miniature (ajoutées avant la mise en place des miniatures).
  const imagesWithoutThumb = resources.filter(
    r => r.type === 'Image' && r.filePath && !r.thumbUrl && !r.thumbError
  );

  // ── Liste filtrée (onglet Ressources) ────────────────────────────────────
  const filtered = useMemo(() => {
    const typeMatch = TYPE_FILTERS.find(t => t.key === typeFilter)?.match || (() => true);
    const terms = normalize(searchTerm).split(/\s+/).filter(Boolean);

    return resources
      .filter(r => typeMatch(r))
      .filter(r => sourceFilter === 'all' || (sourceFilter === 'app' ? isFromApp(r) : !isFromApp(r)))
      .filter(r => statusFilter === 'all' || (statusFilter === 'pending' ? r.status === 'pending' : r.status !== 'pending'))
      .filter(r => {
        if (locationFilter === 'all') return true;
        if (locationFilter === 'none') return isOrphan(r);
        const [kind, id] = locationFilter.split(':');
        return kind === 'folder' ? r.folderId === id : (!r.folderId && r.categoryId === id);
      })
      .filter(r => {
        if (terms.length === 0) return true;
        const haystack = normalize([
          r.title, r.description, r.fileName, r.type, locationOf(r), authorOf(r),
        ].join(' '));
        return terms.every(t => haystack.includes(t));
      })
      .sort((a, b) => toTime(b.dateAdded) - toTime(a.dateAdded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, typeFilter, sourceFilter, statusFilter, locationFilter, searchTerm, folderById, categoryById, courseById, usersById]);

  // Revenir en haut de la liste quand les filtres changent
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [typeFilter, sourceFilter, statusFilter, locationFilter, searchTerm]);

  const visible = filtered.slice(0, visibleCount);
  const orphanCount = resources.filter(isOrphan).length;
  const appCount = resources.filter(isFromApp).length;

  // ── Sélection ─────────────────────────────────────────────────────────────
  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectedResources = resources.filter(r => selected.has(r.id));
  const selectedPending = selectedResources.filter(r => r.status === 'pending');
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));

  const selectAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach(r => next.delete(r.id));
      else filtered.forEach(r => next.add(r.id));
      return next;
    });
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const approveResources = async (list) => {
    const targets = list.filter(r => r.status === 'pending');
    if (targets.length === 0) return;
    if (targets.length > 1 && !window.confirm(`Approuver ${targets.length} ressources ?`)) return;
    const ids = new Set(targets.map(r => r.id));
    setBusyId('__bulk__');
    try {
      const all = [...ids];
      for (let i = 0; i < all.length; i += 400) {
        const batch = writeBatch(db);
        all.slice(i, i + 400).forEach(id => batch.update(doc(db, 'resources', id), { status: 'approved' }));
        await batch.commit();
      }
      setResources(prev => prev.map(r => ids.has(r.id) ? { ...r, status: 'approved' } : r));
    } catch (err) {
      console.error("Erreur lors de l'approbation:", err);
      alert("Impossible d'approuver pour le moment.");
      fetchData();
    } finally {
      setBusyId(null);
    }
  };

  // Suppression définitive : documents Firestore + fichiers Storage
  // (la miniature éventuelle est supprimée par la Cloud Function
  // cleanupResourceFile). Fonctionne aussi pour les envois depuis l'app.
  const deleteResources = async (list, { confirmMessage } = {}) => {
    if (list.length === 0) return false;
    const n = list.length;
    const message = confirmMessage || (n > 1
      ? `Supprimer définitivement ces ${n} ressources ?\nLes fichiers seront aussi effacés.`
      : `Supprimer définitivement « ${list[0].title || 'cette ressource'} » ?\nLe fichier sera aussi effacé.`);
    if (!window.confirm(message)) return false;

    const ids = new Set(list.map(r => r.id));
    setBusyId('__bulk__');
    try {
      const all = [...ids];
      for (let i = 0; i < all.length; i += 400) {
        const batch = writeBatch(db);
        all.slice(i, i + 400).forEach(id => batch.delete(doc(db, 'resources', id)));
        await batch.commit();
      }
      await Promise.all(list.map(r => deleteStorageFile(r.filePath)));

      setResources(prev => prev.filter(r => !ids.has(r.id)));
      setSelected(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      if (previewId && ids.has(previewId)) setPreviewId(null);
      return true;
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      alert('Impossible de supprimer pour le moment.');
      fetchData();
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const rejectResource = (list) => deleteResources(list, {
    confirmMessage: list.length > 1
      ? `Rejeter et supprimer définitivement ces ${list.length} ressources ?`
      : 'Rejeter et supprimer définitivement cette ressource ?',
  });

  const downloadOne = async (r) => {
    if (!r.filePath) {
      if (r.link) window.open(r.link, '_blank', 'noopener');
      return;
    }
    try {
      await downloadBlob(r);
    } catch (err) {
      // CORS non configuré sur le bucket : on ouvre le fichier, l'enregistrement
      // se fait alors avec clic droit › Enregistrer sous.
      window.open(r.filePath, '_blank', 'noopener');
    }
  };

  const downloadSelected = async () => {
    const list = selectedResources.filter(r => r.filePath);
    if (list.length === 0) return;
    setBusyId('__download__');
    let failed = 0;
    try {
      for (const r of list) {
        try {
          await downloadBlob(r);
          // petite pause : certains navigateurs bloquent les rafales de téléchargements
          await new Promise(res => setTimeout(res, 300));
        } catch (err) {
          failed++;
          if (failed === 1 && list.length > 1) break; // probablement CORS : inutile d'insister
        }
      }
    } finally {
      setBusyId(null);
    }
    if (failed > 0) {
      alert(
        "Le navigateur n'a pas pu télécharger les fichiers directement " +
        '(le stockage Firebase doit autoriser ce site : configuration CORS du bucket).\n' +
        'En attendant, utilisez l\'aperçu puis clic droit › Enregistrer sous.'
      );
    }
  };

  // Rattrapage : redéclenche la Cloud Function generateResourceThumbnail
  // pour les images existantes qui n'ont pas encore de miniature.
  const handleGenerateThumbs = async () => {
    const targets = imagesWithoutThumb;
    if (targets.length === 0) return;
    setBusyId('__thumbs__');
    try {
      for (let i = 0; i < targets.length; i += 400) {
        const batch = writeBatch(db);
        targets.slice(i, i + 400).forEach(r => {
          batch.update(doc(db, 'resources', r.id), {
            thumbRequestedAt: serverTimestamp(),
            thumbError: deleteField(),
          });
        });
        await batch.commit();
      }
      alert(`Génération lancée pour ${targets.length} image${targets.length !== 1 ? 's' : ''}.\nRechargez la page dans une minute pour voir les miniatures.`);
    } catch (err) {
      console.error('Erreur lors de la demande de miniatures:', err);
      alert('Impossible de lancer la génération des miniatures pour le moment.');
    } finally {
      setBusyId(null);
    }
  };

  // Suppression d'une catégorie : supprime aussi toutes ses ressources.
  const handleDeleteCategory = async (category) => {
    const categoryResources = resources.filter(r => !r.folderId && r.categoryId === category.id);
    const count = categoryResources.length;
    const linkedCourses = categoryCourseIds(category);
    const sharedNote = linkedCourses.length > 1
      ? `\n\nAttention : cette catégorie est partagée entre ${linkedCourses.length} cours (${linkedCourses.map(courseTitle).join(', ')}). Elle disparaîtra de tous.`
      : '';
    const message = (count > 0
      ? `Supprimer la catégorie « ${category.name} » ET ses ${count} ressource${count !== 1 ? 's' : ''} ?\n\nLes fichiers seront aussi effacés. Cette action est définitive.`
      : `Supprimer la catégorie « ${category.name} » ?`) + sharedNote;
    if (!window.confirm(message)) return;

    setBusyId(category.id);
    try {
      for (let i = 0; i < categoryResources.length; i += 400) {
        const batch = writeBatch(db);
        categoryResources.slice(i, i + 400).forEach(r => batch.delete(doc(db, 'resources', r.id)));
        await batch.commit();
      }
      await deleteDoc(doc(db, 'resource_categories', category.id));
      await Promise.all(categoryResources.map(r => deleteStorageFile(r.filePath)));

      const ids = new Set(categoryResources.map(r => r.id));
      setResources(prev => prev.filter(r => !ids.has(r.id)));
      setCategories(prev => prev.filter(c => c.id !== category.id));
      if (locationFilter === `category:${category.id}`) setLocationFilter('all');
    } catch (err) {
      console.error('Erreur lors de la suppression de la catégorie:', err);
      alert('Impossible de supprimer cette catégorie pour le moment.');
      fetchData();
    } finally {
      setBusyId(null);
    }
  };

  // Suppression d'un dossier : supprime aussi toutes ses ressources
  // (y compris celles en attente de validation) et leurs fichiers.
  const handleDeleteFolder = async (folder) => {
    const folderResources = resources.filter(r => r.folderId === folder.id);
    const count = folderResources.length;
    const message = count > 0
      ? `Supprimer le dossier « ${folder.name} » ET ses ${count} ressource${count !== 1 ? 's' : ''} ?\n\nLes fichiers seront aussi effacés. Cette action est définitive.`
      : `Supprimer le dossier « ${folder.name} » ?`;
    if (!window.confirm(message)) return;

    setBusyId(folder.id);
    try {
      for (let i = 0; i < folderResources.length; i += 400) {
        const batch = writeBatch(db);
        folderResources.slice(i, i + 400).forEach(r => batch.delete(doc(db, 'resources', r.id)));
        await batch.commit();
      }
      await deleteDoc(doc(db, 'resource_folders', folder.id));
      await Promise.all(folderResources.map(r => deleteStorageFile(r.filePath)));

      setResources(prev => prev.filter(r => r.folderId !== folder.id));
      setFolders(prev => prev.filter(f => f.id !== folder.id));
      if (locationFilter === `folder:${folder.id}`) setLocationFilter('all');
    } catch (err) {
      console.error('Erreur lors de la suppression du dossier:', err);
      alert('Impossible de supprimer ce dossier pour le moment.');
      fetchData();
    } finally {
      setBusyId(null);
    }
  };

  const folderMemberLabel = (folder) => {
    if (folder.type === 'general') return 'Visible par tous';
    if (folder.type === 'courses') {
      const names = (folder.courseIds || []).map(courseTitle);
      return names.length > 0 ? `Cours : ${names.join(', ')}` : 'Aucun cours assigné';
    }
    const names = (folder.studentIds || []).map(sid => usersById[sid] || sid);
    return names.length > 0 ? names.join(', ') : 'Aucun élève assigné';
  };

  const showLocation = (value) => {
    setLocationFilter(value);
    setTypeFilter('all');
    setSourceFilter('all');
    setStatusFilter('all');
    setSearchTerm('');
    setView('resources');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Aperçu ────────────────────────────────────────────────────────────────
  const openPreview = (r, list) => {
    setPreviewList(list.map(x => x.id));
    setPreviewId(r.id);
  };
  const previewIndex = previewList.indexOf(previewId);
  const previewResource = previewId ? resources.find(r => r.id === previewId) : null;
  const movePreview = (delta) => {
    if (previewList.length === 0) return;
    const i = previewIndex < 0 ? 0 : previewIndex;
    const nextIndex = (i + delta + previewList.length) % previewList.length;
    setPreviewId(previewList[nextIndex]);
  };

  // ── Données pour l'onglet "Dossiers & catégories" ────────────────────────
  const countFor = (predicate) => resources.filter(predicate).length;
  const sortedCourses = [...courses].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  const categoriesForCourse = (courseId) =>
    categories
      .filter(c => categoryCourseIds(c).includes(courseId))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="resources-loading">
        <div className="spinner"></div>
        <p>Chargement des ressources...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resources-error">
        <div className="error-icon">⚠️</div>
        <h3>Une erreur est survenue</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Réessayer
        </button>
      </div>
    );
  }

  const bulkBusy = busyId === '__bulk__';
  const renderCard = (r, list) => (
    <ResourceCard
      key={r.id}
      resource={r}
      selected={selected.has(r.id)}
      onToggleSelect={toggleSelect}
      onPreview={(res) => openPreview(res, list)}
      onDownload={downloadOne}
      onDelete={deleteResources}
      onApprove={approveResources}
      busy={bulkBusy}
      authorName={authorOf(r)}
      location={locationOf(r)}
    />
  );

  return (
    <div className="resources-container" style={{ paddingBottom: selected.size > 0 ? 90 : undefined }}>
      <div className="resources-header">
        <div className="header-content">
          <h1>Centre de Ressources</h1>
          <p>Toutes les ressources : ajoutées depuis l'admin et envoyées depuis l'app</p>
        </div>
      </div>

      <div className="admin-actions">
        <Link to="/resources/new" className="add-button">
          <FaPlus /> Ajouter une ressource
        </Link>
        <Link to="/folders/new" className="add-button">
          <FaPlus /> Créer un dossier
        </Link>
        <Link to="/categories/new" className="add-button">
          <FaPlus /> Créer une catégorie
        </Link>
        <Link to="/courses" className="add-button">
          <FaPlus /> Gérer les cours
        </Link>
        <Link to="/categories" className="add-button">
          <FaPlus /> Gérer les catégories
        </Link>
        {imagesWithoutThumb.length > 0 && (
          <button
            type="button"
            onClick={handleGenerateThumbs}
            disabled={busyId === '__thumbs__'}
            className="add-button"
            style={{ border: 'none', cursor: 'pointer' }}
            title="Crée des miniatures légères pour les photos déjà en ligne"
          >
            <FaImage /> {busyId === '__thumbs__'
              ? 'Lancement...'
              : `Générer les miniatures (${imagesWithoutThumb.length})`}
          </button>
        )}
      </div>

      {/* ── MODÉRATION : envois en attente ───────────────────────────────── */}
      {pendingResources.length > 0 && (
        <section style={{ ...styles.panel, borderLeft: '4px solid #F59E0B' }}>
          <div style={styles.panelHeader}>
            <button type="button" onClick={() => setPendingOpen(o => !o)} style={styles.panelTitle}>
              <span style={{ fontSize: 22 }}>⏳</span>
              <span>En attente de validation ({pendingResources.length})</span>
              {pendingOpen ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => approveResources(pendingResources)} disabled={bulkBusy}
                style={{ ...styles.primaryBtn, background: '#16A34A' }}>
                <FaCheck /> Tout approuver
              </button>
              <button type="button" onClick={() => rejectResource(pendingResources)} disabled={bulkBusy}
                style={{ ...styles.primaryBtn, background: '#DC2626' }}>
                <FaTimes /> Tout rejeter
              </button>
            </div>
          </div>
          {pendingOpen && (
            <div style={styles.grid}>
              {pendingResources.map(r => renderCard(r, pendingResources))}
            </div>
          )}
        </section>
      )}

      {/* ── ONGLETS ───────────────────────────────────────────────────────── */}
      <div style={styles.tabs}>
        <button type="button" onClick={() => setView('resources')}
          style={{ ...styles.tab, ...(view === 'resources' ? styles.tabActive : null) }}>
          Ressources ({resources.length})
        </button>
        <button type="button" onClick={() => setView('structure')}
          style={{ ...styles.tab, ...(view === 'structure' ? styles.tabActive : null) }}>
          Dossiers & catégories ({folders.length + categories.length})
        </button>
      </div>

      {view === 'resources' && (
        <section style={styles.panel}>
          {/* Filtres */}
          <div style={styles.filters}>
            <input
              type="search"
              placeholder="Rechercher (titre, fichier, cours, dossier, élève…)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} style={styles.select}>
              <option value="all">Tous les emplacements</option>
              {orphanCount > 0 && <option value="none">⚠️ Sans emplacement ({orphanCount})</option>}
              {folders.length > 0 && (
                <optgroup label="Dossiers">
                  {[...folders].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(f => (
                    <option key={f.id} value={`folder:${f.id}`}>
                      {f.name} ({countFor(r => r.folderId === f.id)})
                    </option>
                  ))}
                </optgroup>
              )}
              {sortedCourses.map(c => {
                const cats = categoriesForCourse(c.id);
                if (cats.length === 0) return null;
                return (
                  <optgroup key={c.id} label={`Cours · ${c.title || 'Sans titre'}`}>
                    {cats.map(cat => (
                      <option key={`${c.id}-${cat.id}`} value={`category:${cat.id}`}>
                        {cat.name} ({countFor(r => !r.folderId && r.categoryId === cat.id)})
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={styles.select}>
              <option value="all">Toutes les sources</option>
              <option value="app">Envoyées depuis l'app ({appCount})</option>
              <option value="admin">Ajoutées depuis l'admin</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Publiées</option>
            </select>
          </div>

          <div style={styles.chips}>
            {TYPE_FILTERS.map(t => (
              <button key={t.key} type="button" onClick={() => setTypeFilter(t.key)}
                style={{ ...styles.chip, ...(typeFilter === t.key ? styles.chipActive : null) }}>
                {t.label} ({countFor(t.match)})
              </button>
            ))}
          </div>

          <div style={styles.listHeader}>
            <span style={{ color: '#4B5563' }}>
              {filtered.length} ressource{filtered.length !== 1 ? 's' : ''}
              {locationFilter !== 'all' && (
                <button type="button" onClick={() => setLocationFilter('all')} style={styles.linkBtn}>
                  × retirer le filtre d'emplacement
                </button>
              )}
            </span>
            {filtered.length > 0 && (
              <button type="button" onClick={selectAllFiltered} style={styles.linkBtn}>
                {allFilteredSelected ? 'Tout désélectionner' : `Tout sélectionner (${filtered.length})`}
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
              Aucune ressource ne correspond à ces filtres.
            </div>
          ) : (
            <>
              <div style={styles.grid}>
                {visible.map(r => renderCard(r, filtered))}
              </div>
              {filtered.length > visibleCount && (
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <button type="button" onClick={() => setVisibleCount(c => c + PAGE_SIZE)} style={styles.secondaryBtn}>
                    Afficher plus ({filtered.length - visibleCount} restantes)
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {view === 'structure' && (
        <section style={styles.panel}>
          <h3 style={styles.sectionTitle}><FaUserFriends /> Dossiers</h3>
          {folders.length === 0 ? (
            <p style={{ color: '#6B7280' }}>Aucun dossier pour l'instant.</p>
          ) : (
            [...folders].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(folder => {
              const total = countFor(r => r.folderId === folder.id);
              const pending = countFor(r => r.folderId === folder.id && r.status === 'pending');
              return (
                <div key={folder.id} style={styles.row}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{folder.name}</div>
                    <div style={styles.rowMeta}>
                      {folderMemberLabel(folder)} · {total} ressource{total !== 1 ? 's' : ''}
                      {pending > 0 ? ` (dont ${pending} en attente)` : ''}
                      {folder.allowStudentUploads ? ' · envois élèves autorisés' : ''}
                    </div>
                  </div>
                  <div style={styles.rowActions}>
                    <button type="button" onClick={() => showLocation(`folder:${folder.id}`)} style={styles.primaryBtn}>
                      <FaEye /> Voir les fichiers
                    </button>
                    <Link to={`/resources/new?folderId=${folder.id}`} style={styles.secondaryBtn}>
                      <FaPlus /> Ajouter
                    </Link>
                    <Link to={`/folders/edit/${folder.id}`} style={styles.secondaryBtn}>
                      <FaEdit /> Modifier
                    </Link>
                    <button type="button" onClick={() => handleDeleteFolder(folder)} disabled={busyId === folder.id}
                      style={{ ...styles.secondaryBtn, color: '#DC2626', borderColor: '#FCA5A5' }}>
                      <FaTrash /> {busyId === folder.id ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              );
            })
          )}

          <h3 style={{ ...styles.sectionTitle, marginTop: 32 }}><FaFolder /> Cours et catégories</h3>
          {sortedCourses.map(course => {
            const cats = categoriesForCourse(course.id);
            return (
              <div key={course.id} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: PURPLE, margin: '12px 0 6px' }}>{course.title || 'Cours sans titre'}</div>
                {cats.length === 0 ? (
                  <p style={{ ...styles.rowMeta, marginLeft: 12 }}>Aucune catégorie pour ce cours.</p>
                ) : cats.map(category => {
                  const total = countFor(r => !r.folderId && r.categoryId === category.id);
                  const shared = categoryCourseIds(category).length > 1;
                  return (
                    <div key={`${course.id}-${category.id}`} style={styles.row}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{category.name}</div>
                        <div style={styles.rowMeta}>
                          {total} ressource{total !== 1 ? 's' : ''}
                          {shared ? ` · partagée avec ${categoryCourseIds(category).length} cours` : ''}
                        </div>
                      </div>
                      <div style={styles.rowActions}>
                        <button type="button" onClick={() => showLocation(`category:${category.id}`)} style={styles.primaryBtn}>
                          <FaEye /> Voir les fichiers
                        </button>
                        <Link to={`/resources/new?courseId=${course.id}&categoryId=${category.id}`} style={styles.secondaryBtn}>
                          <FaPlus /> Ajouter
                        </Link>
                        <Link to={`/categories/edit/${category.id}`} style={styles.secondaryBtn}>
                          <FaEdit /> Modifier
                        </Link>
                        <button type="button" onClick={() => handleDeleteCategory(category)} disabled={busyId === category.id}
                          style={{ ...styles.secondaryBtn, color: '#DC2626', borderColor: '#FCA5A5' }}>
                          <FaTrash /> {busyId === category.id ? 'Suppression...' : 'Supprimer'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>
      )}

      {/* ── BARRE D'ACTIONS GROUPÉES ─────────────────────────────────────── */}
      {selected.size > 0 && (
        <div style={styles.bulkBar}>
          <strong>{selected.size} sélectionnée{selected.size > 1 ? 's' : ''}</strong>
          <button type="button" onClick={() => setSelected(new Set())} style={styles.linkBtnLight}>
            Désélectionner
          </button>
          <div style={{ flex: 1 }} />
          {selectedPending.length > 0 && (
            <button type="button" onClick={() => approveResources(selectedPending)} disabled={bulkBusy}
              style={{ ...styles.primaryBtn, background: '#16A34A' }}>
              <FaCheck /> Approuver ({selectedPending.length})
            </button>
          )}
          <button type="button" onClick={downloadSelected} disabled={busyId === '__download__'}
            style={{ ...styles.primaryBtn, background: '#374151' }}>
            <FaDownload /> {busyId === '__download__' ? 'Téléchargement...' : 'Télécharger'}
          </button>
          <button type="button" onClick={() => deleteResources(selectedResources)} disabled={bulkBusy}
            style={{ ...styles.primaryBtn, background: '#DC2626' }}>
            <FaTrash /> {bulkBusy ? 'Suppression...' : `Supprimer (${selected.size})`}
          </button>
        </div>
      )}

      {/* ── APERÇU PLEIN ÉCRAN ───────────────────────────────────────────── */}
      {previewResource && (
        <PreviewModal
          resource={previewResource}
          index={previewIndex < 0 ? 0 : previewIndex}
          total={previewList.length}
          onClose={() => setPreviewId(null)}
          onPrev={() => movePreview(-1)}
          onNext={() => movePreview(1)}
          onDownload={downloadOne}
          onDelete={deleteResources}
          onApprove={approveResources}
          location={locationOf(previewResource)}
          authorName={authorOf(previewResource)}
        />
      )}
    </div>
  );
}

const styles = {
  panel: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: 20,
    marginBottom: 24,
  },
  panelHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
  },
  panelTitle: {
    display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0,
    cursor: 'pointer', fontSize: 20, fontWeight: 700, color: '#111827', textAlign: 'left',
  },
  tabs: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  tab: {
    padding: '10px 18px', borderRadius: 999, border: '1px solid #E5E7EB', background: '#fff',
    cursor: 'pointer', fontWeight: 600, color: '#374151', fontSize: 15,
  },
  tabActive: { background: PURPLE, color: '#fff', borderColor: PURPLE },
  filters: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  searchInput: {
    flex: '1 1 280px', padding: '10px 14px', borderRadius: 10, border: '1px solid #D1D5DB', fontSize: 15,
  },
  select: {
    flex: '0 1 220px', padding: '10px 12px', borderRadius: 10, border: '1px solid #D1D5DB',
    fontSize: 14, background: '#fff', minWidth: 0,
  },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  chip: {
    padding: '6px 14px', borderRadius: 999, border: '1px solid #E5E7EB', background: '#F9FAFB',
    cursor: 'pointer', fontSize: 14, color: '#374151',
  },
  chipActive: { background: '#EDE9FE', borderColor: PURPLE, color: PURPLE, fontWeight: 600 },
  listHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    flexWrap: 'wrap', margin: '8px 0 12px', fontSize: 14,
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginTop: 16,
  },
  card: {
    border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff',
    display: 'flex', flexDirection: 'column', transition: 'box-shadow .15s, border-color .15s',
  },
  cardSelected: { borderColor: PURPLE, boxShadow: `0 0 0 2px ${PURPLE}` },
  cardPreview: {
    position: 'relative', height: 160, background: '#F3F4F6', cursor: 'pointer', overflow: 'hidden',
  },
  iconPreview: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', color: '#fff',
  },
  playBadge: {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, pointerEvents: 'none',
  },
  checkbox: {
    position: 'absolute', top: 8, left: 8, background: 'rgba(255,255,255,0.92)', borderRadius: 6,
    padding: 4, display: 'flex', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  badges: { position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' },
  badge: {
    color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  cardBody: { padding: 10, display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  cardTitle: {
    fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardMeta: {
    fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardActions: { display: 'flex', gap: 4, marginTop: 8, alignItems: 'center' },
  iconBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
    borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#374151', cursor: 'pointer',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#fff',
    background: PURPLE, border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600,
    cursor: 'pointer', fontSize: 14, textDecoration: 'none',
  },
  secondaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#374151',
    background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 12px', fontWeight: 600,
    cursor: 'pointer', fontSize: 14, textDecoration: 'none',
  },
  linkBtn: {
    background: 'none', border: 'none', color: PURPLE, cursor: 'pointer', fontWeight: 600, fontSize: 14, marginLeft: 8,
  },
  linkBtnLight: {
    background: 'none', border: 'none', color: '#E5E7EB', cursor: 'pointer', fontSize: 14, textDecoration: 'underline',
  },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 12px', color: '#111827' },
  row: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 14px',
    border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 8,
  },
  rowMeta: { fontSize: 13, color: '#6B7280', marginTop: 2 },
  rowActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  bulkBar: {
    position: 'fixed', left: '50%', bottom: 20, transform: 'translateX(-50%)', zIndex: 1000,
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    background: '#111827', color: '#fff', padding: '12px 16px', borderRadius: 14,
    boxShadow: '0 10px 25px rgba(0,0,0,0.25)', width: 'min(900px, calc(100vw - 32px))',
  },
  modalBackdrop: {
    position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.88)',
    display: 'flex', flexDirection: 'column',
  },
  modalTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
    padding: '12px 16px', color: '#fff', flexWrap: 'wrap',
  },
  modalCenter: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 72px 24px', minHeight: 0,
  },
  modalMedia: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 },
  modalFallback: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
  modalBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, color: '#fff', background: 'rgba(255,255,255,0.15)',
    border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
  },
  navBtn: {
    position: 'fixed', top: '50%', transform: 'translateY(-50%)', width: 48, height: 48, borderRadius: '50%',
    border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 20, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};

export default Resources;